import React, { useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Button, Dropdown, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import BlackoutScopeTag from "ui/Blackout/BlackoutScopeTag"
import BookingsActionAlert from "ui/Blackout/BookingsActionAlert"
import ReplaceResourceModal from "ui/Blackout/ReplaceResourceModal"
import RecurrenceLine from "ui/Blackout/RecurrenceLine"
import { BLACKOUT_SCOPES, formatEntryWithTime, formatRecurrence, toRecurrence } from "helpers/blackouts"
import { usePermissions } from "hooks/usePermissions"
import { getRecurrencePreview } from "pagesV2/pro/Settings/Blackout/utils"
import styles from "./BlackoutDayCard.module.css"

const RESOURCE_SCOPES = [BLACKOUT_SCOPES.CREW, BLACKOUT_SCOPES.EQUIPMENT]

/** How many upcoming dates the card lists before "…" — as many as a Settings row. */
const NEXT_DATES_IN_CARD = 3

/** Scope -> the copy on its reassignment button. */
const REPLACE_LABEL = {
  [BLACKOUT_SCOPES.CREW]: { key: "calendar.blackout.replace_crew", fallback: "Replace Crew" },
  [BLACKOUT_SCOPES.EQUIPMENT]: { key: "calendar.blackout.replace_equipment", fallback: "Replace Equipment" }
}

/**
 * Scope -> what its resources are called in the summary line.
 *
 * The design names the kind ("1 crew member unavailable", "2 boats unavailable"). Crew is a
 * single kind so it names itself; equipment is not — "boats" is the category of those particular
 * assets, and the feed sends no category per resource, so equipment stays generic until it does.
 *
 * Singular and plural are separate keys throughout: the project is on i18next 19 with the v3 JSON
 * format, where neither plural convention already in `en.json` resolves.
 */
const RESOURCE_NOUN = {
  [BLACKOUT_SCOPES.CREW]: {
    single: { key: "calendar.blackout.crew_unavailable_one", fallback: "1 crew member unavailable" },
    plural: { key: "calendar.blackout.crew_unavailable", fallback: "{{count}} crew members unavailable" }
  },
  [BLACKOUT_SCOPES.EQUIPMENT]: {
    single: { key: "calendar.blackout.equipment_unavailable_one", fallback: "1 item unavailable" },
    plural: { key: "calendar.blackout.equipment_unavailable", fallback: "{{count}} items unavailable" }
  }
}

/**
 * One blackout inside the day details panel.
 *
 * Collapsed it states what is unavailable; expanded it lists the resources and listings.
 * A business blackout has nothing to expand — every listing is affected, and the card says so.
 */
const BlackoutDayCard = ({ blackout, date, menuItems, onReview, onReplaced, canReplace = true }) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isReplaceOpen, setIsReplaceOpen] = useState(false)
  const { hasFullAccessFlag } = usePermissions()

  const { scope, label, reason, listings = [], resources = [], all_listings: isAllListings } = blackout
  const recurrence = toRecurrence(blackout)
  const recurrenceLabel = formatRecurrence(recurrence, t)
  const isResourceScope = RESOURCE_SCOPES.includes(scope)
  const canExpand = !isAllListings && (listings.length > 0 || resources.length > 0)
  const replaceLabel = REPLACE_LABEL[scope]
  // Same rule as the alert above it: once every stranded booking is reassigned the server stops
  // counting the blackout, and there is nothing left for the sheet to show. Reassigning bookings
  // is managing the blackout, which only the guide/admin does.
  const isReplaceVisible = Boolean(hasFullAccessFlag && canReplace && replaceLabel && blackout.bookings_requiring_action)

  // The record's own span when the feed sends it, so a two-day closure reads "Jul 6-7, 2026"
  // rather than the single day this card happens to be rendered on.
  const entry = {
    start_date: blackout.start_date || date,
    end_date: blackout.end_date || date,
    start_time: blackout.start_time,
    end_time: blackout.end_time
  }
  const dateLabel = formatEntryWithTime(entry)

  /**
   * The same "Next:" line as the Settings list. The feeds resolve a repeat to the occurrence being
   * viewed and do not send the series' first date, so the dates run from this occurrence and the
   * total is dropped — counted from here it would read as the whole series.
   *
   * The viewed day can be the rule's last: the preview's form check ("Ends after the first date")
   * would then reject it, so that day is listed on its own rather than dropping the line.
   */
  const recurrencePreview = useMemo(() => {
    if (recurrence?.ends_on && recurrence.ends_on === entry.start_date) {
      const lastOccurrence = { start_date: entry.start_date, end_date: entry.end_date }
      return { next: [lastOccurrence], last: lastOccurrence, total: null, isTotalCapped: false }
    }
    const preview = getRecurrencePreview(recurrence, [entry], { nextCount: NEXT_DATES_IN_CARD })
    return preview && { ...preview, total: null, isTotalCapped: false }
    // `recurrence` and `entry` are rebuilt every render; the feed record they come from is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blackout, date])

  const listingsAffected =
    listings.length === 1
      ? t("calendar.blackout.listings_affected_one", { defaultValue: "1 listing affected" })
      : t("calendar.blackout.listings_affected", { defaultValue: "{{count}} listings affected", count: listings.length })

  let summary
  if (isAllListings) {
    summary = t("calendar.blackout.all_listings_affected", { defaultValue: "All listings affected" })
  } else if (isResourceScope) {
    // "1 crew member unavailable + 6 listings affected" — the two halves are counted separately,
    // so they are built separately and joined by the design's "+".
    const noun = RESOURCE_NOUN[scope][resources.length === 1 ? "single" : "plural"]
    const unavailable = t(noun.key, { defaultValue: noun.fallback, count: resources.length })
    summary = `${unavailable} + ${listingsAffected}`
  } else {
    summary = listingsAffected
  }

  const renderList = (heading, items, getLabel) => {
    if (!items.length) return null
    return (
      <>
        <span className={styles.detailHeading}>{heading}</span>
        <ul className={styles.detailList}>
          {items.map((item) => (
            <li key={item.id} className={styles.detailItem}>
              {getLabel(item)}
            </li>
          ))}
        </ul>
      </>
    )
  }

  return (
    <article className={styles.card}>
      <Flex align="center" gap="var(--spacing-xs)" className={styles.headerRow}>
        <Flex align="center" gap="var(--spacing-xs)" className={styles.headerMain}>
          <span className={styles.dateLabel}>{dateLabel}</span>
        </Flex>
        <BlackoutScopeTag scope={scope} label={label} tone="solid" />
        {menuItems?.length > 0 && (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
            <button
              type="button"
              className={styles.menuButton}
              aria-label={t("calendar.blackout.card_actions", { defaultValue: "Blackout actions" })}
            >
              <Icon name="MoreHorizontal" width={16} height={16} />
            </button>
          </Dropdown>
        )}
      </Flex>

      {recurrenceLabel && <RecurrenceLine label={recurrenceLabel} preview={recurrencePreview} nextCount={NEXT_DATES_IN_CARD} />}

      {reason && (
        <span className={styles.reasonPill}>
          <Icon name="Clipboard" width={16} height={16} color="var(--color-text-secondary-dark)" />
          {reason}
        </span>
      )}

      <Flex align="center" gap="var(--spacing-xxs)" className={styles.summaryRow}>
        <span className={styles.summaryText}>{summary}</span>
        {canExpand && (
          <button type="button" className={styles.linkButton} onClick={() => setIsExpanded((previous) => !previous)}>
            {isExpanded
              ? t("calendar.blackout.collapse", { defaultValue: "Collapse" })
              : t("calendar.blackout.details", { defaultValue: "Details" })}
          </button>
        )}
      </Flex>

      {isExpanded && (
        <div className={styles.detail}>
          {renderList(
            scope === BLACKOUT_SCOPES.CREW
              ? t("calendar.blackout.detail_crew", { defaultValue: "Crew" })
              : t("calendar.blackout.detail_equipment", { defaultValue: "Equipment" }),
            isResourceScope ? resources : [],
            (resource) => resource.label
          )}
          {renderList(t("calendar.blackout.detail_listings", { defaultValue: "Listings" }), listings, (listing) => listing.title)}
        </div>
      )}

      <BookingsActionAlert
        count={blackout.bookings_requiring_action}
        onReview={onReview}
        className={styles.alert}
      />

      {isReplaceVisible && (
        <Button type="primary" size="small" onClick={() => setIsReplaceOpen(true)} className={styles.replaceButton}>
          {t(replaceLabel.key, { defaultValue: replaceLabel.fallback })}
        </Button>
      )}

      {isReplaceOpen && (
        <ReplaceResourceModal
          isOpen={isReplaceOpen}
          scope={scope}
          blackoutId={blackout.id}
          onCancel={() => setIsReplaceOpen(false)}
          onReplaced={onReplaced}
        />
      )}
    </article>
  )
}

BlackoutDayCard.propTypes = {
  blackout: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    scope: PropTypes.string,
    label: PropTypes.string,
    reason: PropTypes.string,
    recurring_yearly: PropTypes.bool,
    recurrence: PropTypes.shape({ frequency: PropTypes.string, interval: PropTypes.number, weekdays: PropTypes.array, ends_on: PropTypes.string }),
    start_time: PropTypes.string,
    end_time: PropTypes.string,
    all_listings: PropTypes.bool,
    listings: PropTypes.array,
    resources: PropTypes.array,
    bookings_requiring_action: PropTypes.oneOfType([PropTypes.number, PropTypes.bool])
  }).isRequired,
  /** The day being shown, "YYYY-MM-DD" — the card states this day, not the whole record. */
  date: PropTypes.string.isRequired,
  menuItems: PropTypes.array,
  onReview: PropTypes.func,
  /** Fired once bookings have been reassigned, so the caller can refetch what showed the conflict. */
  onReplaced: PropTypes.func,
  /** False where the record cannot go through the blackout replacement endpoints — a crew
   *  member's time off, whose id is not a blackout id. */
  canReplace: PropTypes.bool
}

export default BlackoutDayCard
